/**
 * Redux Thunk API Call Handler
 *
 * Generic types for type-safe Redux async actions.
 *
 * @template TRequest - Request payload type
 * @template TResponse - Response data type
 * @template TError - Error object type
 *
 * @see src/services/api.ts - API service layer
 * @see .github/copilot-instructions.md#api--services
 */

import { Dispatch } from 'redux';
import { ApiError } from 'services/api';

/**
 * Redux action creator function type
 */
type ActionCreator<TPayload> = (payload: TPayload) => {
  type: string;
  payload: TPayload;
};

/**
 * Arguments for callApi function
 *
 * @template TRequest - Request payload type
 * @template TResponse - API response type
 * @template TError - Error type (defaults to ApiError)
 */
type CallApiArgumentsType<TRequest = unknown, TResponse = unknown, TError = ApiError> = {
  /** API function that returns a promise with the response */
  api: (() => Promise<TResponse>) | null;

  actionEntity: {
    /** Action creator for request start */
    request: ActionCreator<TRequest>;
    /** Action creator for success */
    success: ActionCreator<ResponseReturnType<TRequest, TResponse>>;
    /** Action creator for failure */
    failure: ActionCreator<ErrorReturnType<TRequest, TError>>;
    /** Redux dispatch function */
    dispatch: Dispatch;
    /** Request payload to pass to action creators */
    requestPayload: TRequest;
  };

  errorEntity: {
    /** Optional function to enhance/transform errors before dispatch */
    enhanceErrorFn: null | ((error: TError) => TError);
    /** Error type identifier for error tracking */
    errorType: string;
  };

  /**
   * Custom try/catch wrapper for advanced use cases
   * 99% of the time you won't need this
   * @example async () => { await callApi(); dispatch(success(payload)); }
   */
  customTryCallApiFn?: (() => Promise<void>) | null;
};

/**
 * Response object returned on successful API call
 *
 * @template TRequest - Original request payload type
 * @template TResponse - API response data type
 */
type ResponseReturnType<TRequest = unknown, TResponse = unknown> = {
  /** Original request payload for correlation */
  requestPayload: TRequest;
  /** Response data from API */
  responsePayload: TResponse;
};

/**
 * Error object returned on failed API call
 *
 * @template TRequest - Original request payload type
 * @template TError - Error object type
 */
type ErrorReturnType<TRequest = unknown, TError = ApiError> = TError & {
  /** Original request payload for correlation */
  requestPayload: TRequest;
  /** Error type identifier */
  type?: string;
};

/**
 * Return type for callApi function
 *
 * @template TRequest - Request payload type
 * @template TResponse - Response data type
 * @template TError - Error type
 */
export type CallAPIReturnType<TRequest = unknown, TResponse = unknown, TError = ApiError> = {
  response?: ResponseReturnType<TRequest, TResponse>;
  error?: ErrorReturnType<TRequest, TError>;
};

/**
 * Generic Redux thunk API call handler
 *
 * Handles the full Redux async action lifecycle:
 * 1. Dispatch request action
 * 2. Call API
 * 3. Dispatch success or failure action
 * 4. Return result for further handling
 *
 * @template TRequest - Request payload type
 * @template TResponse - API response type
 * @template TError - Error type
 *
 * @param api - API function that returns a promise
 * @param actionEntity - Redux action creators and dispatch
 * @param errorEntity - Error handling configuration
 * @param customTryCallApiFn - Optional custom try/catch wrapper
 * @returns Promise with response or error
 *
 * @example
 * ```typescript
 * interface FetchUserRequest { userId: string }
 * interface FetchUserResponse { id: string; name: string; email: string }
 *
 * const result = await callApi<FetchUserRequest, FetchUserResponse>(
 *   () => getAPI<FetchUserResponse>('/users/123', {}, token),
 *   {
 *     request: fetchUserRequest,
 *     success: fetchUserSuccess,
 *     failure: fetchUserFailure,
 *     dispatch,
 *     requestPayload: { userId: '123' }
 *   },
 *   {
 *     enhanceErrorFn: null,
 *     errorType: 'FETCH_USER_ERROR'
 *   }
 * );
 * ```
 */
export const callApi = async <TRequest = unknown, TResponse = unknown, TError = ApiError>(
  api: (() => Promise<TResponse>) | null,
  {
    request,
    success,
    failure,
    dispatch,
    requestPayload,
  }: CallApiArgumentsType<TRequest, TResponse, TError>['actionEntity'],
  { enhanceErrorFn, errorType }: CallApiArgumentsType<TRequest, TResponse, TError>['errorEntity'],
  customTryCallApiFn?: (() => Promise<void>) | null,
): Promise<CallAPIReturnType<TRequest, TResponse, TError>> => {
  // Dispatch request action
  dispatch(request(requestPayload));

  try {
    if (customTryCallApiFn) {
      // Custom try/catch wrapper for advanced use cases
      await customTryCallApiFn();
      return {}; // Custom function handles dispatch
    } else {
      if (!api) {
        throw new Error('API function is required when customTryCallApiFn is not provided');
      }

      // Call API and get response
      const response = await api();

      // Create success payload with request correlation
      const successPayload: ResponseReturnType<TRequest, TResponse> = {
        requestPayload: requestPayload,
        responsePayload: response,
      };

      // Dispatch success action
      dispatch(success(successPayload));

      return { response: successPayload };
    }
  } catch (error: unknown) {
    // Type-safe error handling with proper type conversions
    let typedError: ErrorReturnType<TRequest, TError>;

    if (error instanceof Error) {
      // Standard Error object - use unknown for safe conversion
      typedError = {
        ...error,
        type: errorType,
        requestPayload,
      } as unknown as ErrorReturnType<TRequest, TError>;
    } else if (typeof error === 'object' && error !== null) {
      // Object-like error (e.g., from Axios) - use unknown for safe conversion
      typedError = {
        ...(error as TError),
        type: errorType,
        requestPayload,
      } as unknown as ErrorReturnType<TRequest, TError>;
    } else {
      // Primitive or unknown error type - use unknown for safe conversion
      typedError = {
        name: 'UnknownError',
        message: String(error),
        type: errorType,
        requestPayload,
      } as unknown as ErrorReturnType<TRequest, TError>;
    }

    // Enhance error if custom enhancer provided
    const enhancedError = enhanceErrorFn
      ? enhanceErrorFn(typedError as unknown as TError)
      : typedError;

    // Log error for debugging
    console.error(
      'Failed with error: ',
      (enhancedError as unknown as ApiError)?.devMessage || enhancedError || 'Unknown error',
    );

    // Create failure payload
    const failurePayload: ErrorReturnType<TRequest, TError> = {
      ...enhancedError,
      requestPayload,
      type: errorType,
    } as unknown as ErrorReturnType<TRequest, TError>;

    // Dispatch failure action
    dispatch(failure(failurePayload));

    return { error: failurePayload };
  }
};
